import { useMemo } from "react";
import { Area, Bar, CartesianGrid, ComposedChart, ErrorBar, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartTooltip } from "@/components/dashboard/ChartTooltip";
import { calendarOverlays } from "@/components/exec/Charts";
import { CHART, fmtInt, fmtMYRShort, fmtDate, DAY_FULL, tickStyle } from "@/lib/format";
import { useScope } from "@/lib/scope";
import { cn } from "@/lib/utils";

const kFmt = (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v);
const uniqNames = (events = []) => [...new Set(events.map((e) => e.name))];

/* ------------------------------------------------------------------ daily forecast with history, fit and P10-P90 band */
export const ForecastChart = ({ recent = [], daily = [], events = [], anchor, height = 360, showFitted = true, testId }) => {
  const { overlay } = useScope();
  const data = useMemo(() => {
    const hist = recent.map((r) => ({ date: r.date, day: r.day, actual: r.actual, fitted: showFitted ? r.fitted : null, yhat: null, band: null, names: r.names || [], kind: "actual" }));
    const fc = daily.map((r) => ({ date: r.date, day: r.day, actual: null, fitted: null, yhat: r.yhat, band: [r.lo, r.hi], lo: r.lo, hi: r.hi, names: uniqNames(r.events), revenue: r.revenue, kind: "forecast" }));
    // join the lines at the anchor: give the first forecast point's predecessor a yhat so the line is continuous
    if (hist.length && fc.length) {
      const last = hist[hist.length - 1];
      last.yhat = last.actual ?? last.fitted ?? null;
      last.band = last.yhat !== null ? [last.yhat, last.yhat] : null;
    }
    return [...hist, ...fc];
  }, [recent, daily, showFitted]);
  const maxY = Math.max(0, ...data.map((d) => Math.max(d.actual || 0, d.hi || 0)));
  const interval = data.length > 120 ? 13 : data.length > 60 ? 6 : data.length > 30 ? 3 : 0;
  return (
    <div className="chart-frame" data-testid={testId || "forecast-chart"} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 12, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          {overlay && calendarOverlays(events, data, { dotY: maxY * 1.02 })}
          {anchor && <ReferenceLine x={anchor} stroke={CHART.gold} strokeDasharray="3 3" strokeOpacity={0.8} label={{ value: "forecast →", position: "insideTopRight", fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />}
          <XAxis dataKey="date" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} interval={interval} tickFormatter={fmtDate} minTickGap={18} />
          <YAxis tick={tickStyle} tickLine={false} axisLine={false} tickFormatter={kFmt} width={48} />
          <Tooltip
            content={
              <ChartTooltip
                labelFormatter={(l, p) => {
                  const row = p?.[0]?.payload;
                  return `${DAY_FULL[row?.day] || ""} ${fmtDate(l)}${row?.names?.length ? ` · ${row.names.slice(0, 2).join(" · ")}` : ""}${row?.kind === "forecast" ? " · forecast" : ""}`;
                }}
                formatter={(v, name, p) => {
                  if (name === "band") return v ? [`${fmtInt(v[0])} – ${fmtInt(v[1])}`, "80% range"] : null;
                  if (name === "yhat") return [fmtInt(v), p.payload.revenue ? `Expected (rev ${fmtMYRShort(p.payload.revenue)})` : "Expected visitors"];
                  if (name === "fitted") return [fmtInt(v), "Model fit"];
                  return [fmtInt(v), "Actual visitors"];
                }}
              />
            }
          />
          <Area type="monotone" dataKey="band" stroke="none" fill={CHART.gold} fillOpacity={0.14} connectNulls={false} isAnimationActive={false} activeDot={false} />
          {showFitted && <Line type="monotone" dataKey="fitted" stroke={CHART.gray} strokeWidth={1.25} strokeDasharray="3 3" dot={false} connectNulls isAnimationActive={false} />}
          <Line type="monotone" dataKey="actual" stroke={CHART.sky} strokeWidth={2} dot={data.length <= 40 ? { r: 2.5, fill: CHART.sky, strokeWidth: 0 } : false} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="yhat" stroke={CHART.gold} strokeWidth={2.25} dot={false} connectNulls isAnimationActive={false} activeDot={{ r: 4, strokeWidth: 2, stroke: CHART.gold, fill: "hsl(var(--background))" }} />
          <Legend verticalAlign="top" height={24} iconType="plainline" formatter={(v) => ({ actual: "Actual", fitted: "Model fit", yhat: "Forecast", band: "80% range" }[v] || v)} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ------------------------------------------------------------------ weekly / monthly rollup with error bars */
export const ForecastBuckets = ({ buckets = [], actualBuckets = [], labelKey = "label", height = 320, revenue = false, testId }) => {
  const data = useMemo(() => {
    const hist = actualBuckets.map((b) => ({ label: b[labelKey], actual: b.visitors, value: null, err: null, days: b.days, kind: "actual" }));
    const fc = buckets.map((b) => ({
      label: b[labelKey], actual: null, value: revenue ? b.revenue : b.visitors, err: revenue ? (b.revenue !== null ? [b.revenue - b.revenue_lo, b.revenue_hi - b.revenue] : null) : [b.visitors - b.lo, b.hi - b.visitors],
      lo: revenue ? b.revenue_lo : b.lo, hi: revenue ? b.revenue_hi : b.hi, days: b.days, holidays: Array.isArray(b.holidays) ? b.holidays.length : b.holidays, holiday_names: b.holiday_names || b.holidays || [], school: b.school_days, kind: "forecast",
    }));
    return [...hist, ...fc];
  }, [buckets, actualBuckets, labelKey, revenue]);
  const fmt = revenue ? fmtMYRShort : fmtInt;
  return (
    <div className="chart-frame" data-testid={testId || "forecast-buckets"} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 12, right: 8, left: -4, bottom: 0 }} barCategoryGap={data.length > 20 ? 4 : 12}>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          <XAxis dataKey="label" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} interval={data.length > 30 ? 3 : 0} minTickGap={10} />
          <YAxis tick={tickStyle} tickLine={false} axisLine={false} tickFormatter={(v) => (revenue ? fmtMYRShort(v) : kFmt(v))} width={revenue ? 64 : 52} />
          <Tooltip
            cursor={{ fill: "hsl(var(--gold) / 0.06)" }}
            content={
              <ChartTooltip
                labelFormatter={(l, p) => {
                  const row = p?.[0]?.payload;
                  const parts = [l];
                  if (row?.days) parts.push(`${row.days} day${row.days === 1 ? "" : "s"}`);
                  if (row?.holidays) parts.push(`${row.holidays} public holiday${row.holidays === 1 ? "" : "s"}`);
                  if (row?.school) parts.push(`${row.school} school-holiday days`);
                  if (row?.kind === "forecast") parts.push("forecast");
                  return parts.join(" · ");
                }}
                formatter={(v, name, p) => {
                  if (name === "value") return [`${fmt(v)}  (${fmt(p.payload.lo)} – ${fmt(p.payload.hi)})`, revenue ? "Expected revenue" : "Expected visitors"];
                  if (name === "actual") return [fmt(v), "Actual"];
                  return null;
                }}
              />
            }
          />
          <Bar dataKey="actual" fill={CHART.sky} fillOpacity={0.75} radius={[5, 5, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="value" fill={CHART.gold} fillOpacity={0.85} radius={[5, 5, 0, 0]} isAnimationActive={false}>
            <ErrorBar dataKey="err" width={6} strokeWidth={1.25} stroke="hsl(var(--foreground) / 0.55)" direction="y" />
          </Bar>
          <Legend verticalAlign="top" height={24} formatter={(v) => (v === "actual" ? "Actual" : revenue ? "Expected revenue (80% range)" : "Expected visitors (80% range)")} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ------------------------------------------------------------------ backtest: what we would have predicted vs what happened */
export const BacktestChart = ({ rows = [], height = 280, showNaive = false, testId }) => {
  const data = useMemo(() => rows.map((r) => ({ ...r, band: [r.lo, r.hi] })), [rows]);
  const origins = useMemo(() => [...new Set(rows.map((r) => r.origin))], [rows]);
  return (
    <div className="chart-frame" data-testid={testId || "backtest-chart"} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 12, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          {origins.slice(1).map((o) => {
            const first = rows.find((r) => r.origin === o);
            return first ? <ReferenceLine key={o} x={first.date} stroke={CHART.axis} strokeDasharray="2 4" /> : null;
          })}
          <XAxis dataKey="date" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} interval={rows.length > 30 ? 6 : 2} tickFormatter={fmtDate} minTickGap={16} />
          <YAxis tick={tickStyle} tickLine={false} axisLine={false} tickFormatter={kFmt} width={48} />
          <Tooltip
            content={
              <ChartTooltip
                labelFormatter={(l, p) => `${DAY_FULL[p?.[0]?.payload?.day] || ""} ${fmtDate(l)} · predicted on ${fmtDate(p?.[0]?.payload?.origin)}`}
                formatter={(v, name) => {
                  if (name === "band") return v ? [`${fmtInt(v[0])} – ${fmtInt(v[1])}`, "80% range"] : null;
                  if (name === "pred") return [fmtInt(v), "Predicted"];
                  if (name === "naive") return [fmtInt(v), "Same weekday last week"];
                  return [fmtInt(v), "Actual"];
                }}
              />
            }
          />
          <Area type="monotone" dataKey="band" stroke="none" fill={CHART.gold} fillOpacity={0.14} isAnimationActive={false} activeDot={false} />
          <Bar dataKey="actual" fill={CHART.sky} fillOpacity={0.6} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          <Line type="monotone" dataKey="pred" stroke={CHART.gold} strokeWidth={2} dot={{ r: 2.5, fill: CHART.gold, strokeWidth: 0 }} isAnimationActive={false} />
          {showNaive && <Line type="monotone" dataKey="naive" stroke={CHART.gray} strokeWidth={1.25} strokeDasharray="3 3" dot={false} connectNulls isAnimationActive={false} />}
          <Legend verticalAlign="top" height={24} formatter={(v) => ({ actual: "Actual", pred: "Predicted a week earlier", band: "80% range", naive: "Same weekday last week" }[v] || v)} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ------------------------------------------------------------------ weekday factor bars */
export const FactorBars = ({ factors = [], height = 200, testId }) => (
  <div className="chart-frame" data-testid={testId || "factor-bars"} style={{ height }}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={factors} margin={{ top: 8, right: 0, left: -8, bottom: 0 }} barCategoryGap={18}>
        <CartesianGrid stroke={CHART.grid} vertical={false} />
        <ReferenceLine y={1} stroke={CHART.axis} strokeDasharray="3 3" />
        <XAxis dataKey="day" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} />
        <YAxis tick={tickStyle} tickLine={false} axisLine={false} domain={[0, (max) => Math.max(1.2, Math.ceil(max * 10) / 10)]} tickFormatter={(v) => `${Number(v).toFixed(1)}x`} width={52} />
        <Tooltip cursor={{ fill: "hsl(var(--gold) / 0.06)" }} content={<ChartTooltip labelFormatter={(l, p) => `${DAY_FULL[l] || l} · ${p?.[0]?.payload?.n ?? 0} normal days observed`} formatter={(v) => [`${Number(v).toFixed(2)}x average day`, "Weekday factor"]} />} />
        <Bar dataKey="factor" radius={[6, 6, 0, 0]} isAnimationActive={false} fill={CHART.gold} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

/* ------------------------------------------------------------------ accuracy meter */
export const AccuracyMeter = ({ value, label, className, testId }) => {
  const pct = value === null || value === undefined ? 0 : Math.max(0, Math.min(100, value));
  const tone = value === null || value === undefined ? "bg-muted" : pct >= 90 ? "bg-success" : pct >= 80 ? "bg-gold" : pct >= 70 ? "bg-warning" : "bg-danger";
  return (
    <div className={cn("space-y-1", className)} data-testid={testId}>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground"><span>{label}</span><span className="font-mono text-foreground">{value === null || value === undefined ? "—" : `${pct.toFixed(1)}%`}</span></div>
      <div className="h-1.5 w-full rounded-full bg-muted/40 overflow-hidden"><span className={cn("block h-full rounded-full transition-[width] duration-300", tone)} style={{ width: `${pct}%` }} /></div>
    </div>
  );
};
